import { CssBaseline, ThemeProvider } from "@mui/material";
import { SnackbarProvider } from "notistack";
import React, { Suspense, lazy } from "react";
import { Provider } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { Loader } from "./components";
import { Web3ModalProvider } from "./components/Wallet/WalletProvider";
import { AuthProvider, useAuth } from "./context/authContext";
import { LoaderProvider } from "./context/loaderContext";
import { SocketProvider } from "./context/SocketContext";
import Login from "./pages/login";
import store from "./store";
import { themeSettings } from "./theme";
import { GlobalTheme } from "./theme/GlobalTheme";
import NotFound from "./pages/notfound/NotFound";
import TicketDashboard from "pages/tickets/TicketDashboard";
import TicketDetail from "pages/tickets/TicketDetail";

const TryoutModel = lazy(() =>
  import("./components/Menus/dashboard3d/modelCreator")
);
const Home = lazy(() => import("./pages/home/home"));
const NftModelDetails = lazy(() => import("./pages/nftModelDetails"));
const NftDetails = lazy(() => import("./pages/nftDetails"));
const Dashboard3d = lazy(() => import("./components/Menus/dashboard3d"));
const Files = lazy(() => import("./components/Menus/files"));
const ARService = lazy(() => import("./components/Menus/ARService"));
const About = lazy(() => import("./pages/about/about"));
const Tutorials = lazy(() => import("./components/Menus/tutorials"));
const ContactUs = lazy(() => import("./pages/contactUs"));

function App() {
  return (
    <Suspense fallback={<Loader />}>
      <Web3ModalProvider>
        <ThemeProvider theme={themeSettings}>
          <GlobalTheme theme={themeSettings} />
          <SnackbarProvider
            className="snackbar-provider"
            autoHideDuration={3000}
            maxSnack={2}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <CssBaseline />
            <Provider store={store}>
              <BrowserRouter>
                <AuthProvider>
                  <LoaderProvider>
                    <SocketProvider>
                      <Routes>
                        <Route path="*" element={<ProtectedRoutes />} />
                      </Routes>
                    </SocketProvider>
                  </LoaderProvider>
                </AuthProvider>
              </BrowserRouter>
            </Provider>
          </SnackbarProvider>
        </ThemeProvider>
      </Web3ModalProvider>
    </Suspense>
  );
}

function ProtectedRoutes() {
  const { loading, isMobileLandscape } = useAuth();

  if (isMobileLandscape) {
    return <Loader home landScape />;
  }
  if (loading) {
    return <Loader home />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/tickets" element={<TicketDashboard />} />
      <Route path="/tickets/:id" element={<TicketDetail />} />
      <Route path="/contactUs" element={<ContactUs />} />
      <Route path="/:id" element={<NftDetails />} />

      {/* Protect the routes that need NFT ownership */}

      <Route
        path="/:id/:nftId"
        element={<ProtectedRoute element={NftModelDetails} />}
      />
      <Route
        path="/:id/:nftId/3dTools"
        element={<ProtectedRoute element={Dashboard3d} />}
      />
      <Route
        path="/:id/:nftId/tutorials"
        element={<ProtectedRoute element={Tutorials} />}
      />
      <Route
        path="/:id/:nftId/files"
        element={<ProtectedRoute element={Files} />}
      />
      <Route
        path="/:id/:nftId/arTools"
        element={<ProtectedRoute element={ARService} />}
      />
      <Route
        path="/:id/:nftId/3dTools/merch"
        element={<ProtectedRoute element={TryoutModel} />}
      />
      <Route
        path="/:id/:nftId/3dTools/MBACloset"
        element={<ProtectedRoute element={TryoutModel} />}
      />
      <Route
        path="/:id/:nftId/3dTools/testForPrivately"
        element={<Dashboard3d />}
      />
      <Route
        path="/:id/:nftId/3dTools/testForPrivately/merch"
        element={<TryoutModel />}
      />
      <Route
        path="/:id/:nftId/3dTools/testForPrivately/MBACloset"
        element={<TryoutModel />}
      />
      {/* 404 Page Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function ProtectedRoute({ element: Component, ...rest }) {
  const { user } = useAuth();
  const { id, nftId } = useParams();

  const nftOwned = user?.owned[id]?.includes(nftId);

  if (nftId && !nftOwned) {
    return <Navigate to="/" replace />;
  }

  return <Component id={id} nftId={nftId} {...rest} />;
}

export default App;
